import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { RocketLaunch, MagnifyingGlass, X, PlusCircle, PencilSimple } from "@phosphor-icons/react";

const INSURERS = ["AXA", "Allianz", "Generali", "Generali (autre)", "Maaf", "Swiss Life"];
const VOLUMES = [{ v: "", l: "—" }, { v: "faible", l: "Faible" }, { v: "moyen", l: "Moyen" }, { v: "eleve", l: "Élevé" }];

function IcpBadge({ icp }) {
  const c = icp.niveau === "élevé" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
    : icp.niveau === "moyen" ? "bg-orange-500/10 text-orange-400 border-orange-500/30"
    : "bg-zinc-500/10 text-zinc-400 border-zinc-500/30";
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold border ${c}`} data-testid="icp-badge">{icp.score} · {icp.niveau}</span>;
}

export default function AeliamProspection() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ q: "", departement: "", score_min: 0, only_tagged: false });
  const [edit, setEdit] = useState(null); // company being edited

  const load = () => {
    setLoading(true);
    api.get("/aeliam/prospects", { params: { q: f.q || undefined, departement: f.departement || undefined, score_min: f.score_min, only_tagged: f.only_tagged } })
      .then((r) => setRows(r.data)).catch(() => toast.error("Erreur de chargement")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f.score_min, f.only_tagged]);
  useEffect(() => { api.get("/aeliam/kpis").then((r) => setKpis(r.data)); }, []);

  const addToPipeline = async (cid) => {
    try { await api.post("/aeliam/leads", { company_id: cid }); toast.success("Ajouté au pipeline Aeliam"); load(); api.get("/aeliam/kpis").then((r) => setKpis(r.data)); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto space-y-6" data-testid="aeliam-prospection-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center"><RocketLaunch size={22} weight="fill" className="text-white" /></div>
        <div>
          <h1 className="font-head font-bold text-2xl">Prospection Aeliam</h1>
          <p className="text-sm text-muted-foreground">Cabinets à fort potentiel pour le SaaS Aeliam (score ICP).</p>
        </div>
      </div>

      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[["Prospects tagués", kpis.prospects], ["Dans le pipeline", kpis.total_leads], ["Clients", kpis.clients], ["Cabinets affichés", rows.length]].map(([l, v]) => (
            <div key={l} className="bg-card border border-border p-4"><div className="overline text-muted-foreground">{l}</div><div className="font-head font-bold text-2xl">{v}</div></div>
          ))}
        </div>
      )}

      <div className="bg-card border border-border p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="overline text-muted-foreground">Recherche</label>
            <div className="flex items-center gap-2 border border-border px-3 py-2">
              <MagnifyingGlass size={16} className="text-muted-foreground" />
              <input data-testid="aeliam-search" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Nom, ville, SIREN…" className="flex-1 text-sm outline-none" />
            </div>
          </div>
          <div>
            <label className="overline text-muted-foreground">Département</label>
            <input data-testid="aeliam-dept" value={f.departement} onChange={(e) => setF({ ...f, departement: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="33" className="block w-24 border border-border px-3 py-2 text-sm outline-none" />
          </div>
          <div>
            <label className="overline text-muted-foreground">Score ICP min : {f.score_min}</label>
            <input data-testid="aeliam-score-min" type="range" min={0} max={100} step={5} value={f.score_min} onChange={(e) => setF({ ...f, score_min: Number(e.target.value) })} className="block w-40" />
          </div>
          <label className="flex items-center gap-2 text-sm pb-2 cursor-pointer">
            <input data-testid="aeliam-only-tagged" type="checkbox" checked={f.only_tagged} onChange={(e) => setF({ ...f, only_tagged: e.target.checked })} /> Prospects tagués
          </label>
          <button data-testid="aeliam-search-btn" onClick={load} className="px-4 py-2 text-sm font-semibold aeliam-btn-primary">Filtrer</button>
        </div>
      </div>

      <div className="bg-card border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left">
            <tr className="overline text-muted-foreground">
              <th className="px-4 py-3">Cabinet</th><th className="px-4 py-3">Ville</th><th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Effectif</th><th className="px-4 py-3">Âge dir.</th><th className="px-4 py-3">Score ICP</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Chargement…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Aucun prospect. Importez/analysez des cabinets côté Sourcing.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-secondary/40" data-testid={`aeliam-row-${r.id}`}>
                <td className="px-4 py-3 font-medium">{r.nom}{(r.tags || []).includes("Prospect Aeliam") && <span className="ml-2 text-xs text-emerald-600">●</span>}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.ville} {r.departement && `(${r.departement})`}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.type_activite || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.nb_salaries ?? "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.age_dirigeant || "—"}</td>
                <td className="px-4 py-3"><IcpBadge icp={r.icp} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid={`aeliam-edit-${r.id}`} onClick={() => setEdit(r)} className="p-1.5 text-muted-foreground hover:text-foreground" title="Qualifier"><PencilSimple size={16} /></button>
                    {r.in_pipeline ? (
                      <span className="text-xs text-emerald-600 font-medium">Au pipeline</span>
                    ) : (
                      <button data-testid={`aeliam-add-${r.id}`} onClick={() => addToPipeline(r.id)} className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold border border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10 transition-colors">
                        <PlusCircle size={14} /> Pipeline
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && <QualifyModal company={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function QualifyModal({ company, onClose, onSaved }) {
  const [comp, setComp] = useState(company.compagnies_travaillees || []);
  const [vol, setVol] = useState(company.volume_devis_estime || "");
  const [notes, setNotes] = useState(company.aeliam_notes || "");
  const [isProspect, setIsProspect] = useState((company.tags || []).includes("Prospect Aeliam"));
  const [saving, setSaving] = useState(false);
  const toggle = (i) => setComp((c) => c.includes(i) ? c.filter((x) => x !== i) : [...c, i]);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/aeliam/companies/${company.id}`, { compagnies_travaillees: comp, volume_devis_estime: vol, aeliam_notes: notes, is_prospect: isProspect });
      toast.success(`Qualifié · score ICP ${data.icp.score}`); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="aeliam-qualify-modal" onClick={() => !saving && onClose()}>
      <div className="bg-card w-full max-w-md border border-border" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <span className="font-head font-bold">{company.nom}</span>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="overline text-muted-foreground">Compagnies travaillées</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {INSURERS.map((i) => (
                <button key={i} data-testid={`aeliam-insurer-${i}`} onClick={() => toggle(i)}
                  className={`px-2.5 py-1 text-xs font-medium border transition-colors ${comp.includes(i) ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/40" : "border-border text-muted-foreground hover:border-indigo-500/40"}`}>{i}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="overline text-muted-foreground">Volume de devis estimé</label>
            <select data-testid="aeliam-volume" value={vol} onChange={(e) => setVol(e.target.value)} className="block w-full border border-border px-3 py-2 text-sm mt-1 bg-card">
              {VOLUMES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <div>
            <label className="overline text-muted-foreground">Notes</label>
            <textarea data-testid="aeliam-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="block w-full border border-border px-3 py-2 text-sm mt-1 outline-none" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input data-testid="aeliam-is-prospect" type="checkbox" checked={isProspect} onChange={(e) => setIsProspect(e.target.checked)} /> Marquer comme « Prospect Aeliam »
          </label>
          <button data-testid="aeliam-qualify-save" onClick={save} disabled={saving} className="w-full py-2.5 text-sm font-semibold aeliam-btn-primary disabled:opacity-50">{saving ? "Enregistrement…" : "Enregistrer & recalculer le score"}</button>
        </div>
      </div>
    </div>
  );
}
